// Turns app objects (submissions, tool picks) into product-event properties,
// so instrumented components stay one call long. Ids, enums and booleans only.

import { track, trackOnce } from './productEvents';

// These run first in click handlers and load effects (FeedbacksRoot, the
// JeddAI feedback picker): a throw here must never block the real action.
const safely =
  (fn, fallback = () => undefined) =>
  (...args) => {
    try {
      return fn(...args);
    } catch {
      return fallback(...args);
    }
  };

const FEEDBACK_STATUSES = [
  'REVIEWED',
  'CLOSED',
  'FEEDBACK_ACCEPTED',
  'RESUBMISSION_REQUESTED',
];

const upper = (value) => String(value ?? '').toUpperCase();

export const submissionKind = safely(
  (submission) => (submission?.type === 'DOCUMENT' ? 'my_draft' : 'task'),
  () => null
);

export const hasFeedback = safely(
  (submission) =>
    Boolean(submission?.jeddaiFeedbackReceivedAt) ||
    FEEDBACK_STATUSES.includes(upper(submission?.status)),
  () => false
);

export const feedbackBy = safely(
  (submission) => {
    const reviewedBy = upper(submission?.assignment?.reviewedBy);
    if (reviewedBy === 'JEDDAI') return 'jeddai_instant';
    if (reviewedBy === 'JEDDAI_ASSISTED') return 'jeddai_assisted';
    if (reviewedBy === 'TEACHER') return 'teacher';
    if (
      submission?.type === 'DOCUMENT' &&
      submission?.jeddaiFeedbackReceivedAt
    ) {
      return 'jeddai_instant';
    }
    return 'unknown';
  },
  () => 'unknown'
);

export const statusBucket = safely(
  (submission) => {
    const status = upper(submission?.status);
    if (['DRAFT', 'SUBMITTED', 'REVIEWED', 'CLOSED'].includes(status)) {
      return status.toLowerCase();
    }
    return 'other';
  },
  () => 'other'
);

/**
 * The feedback page finished loading a submission: draft_opened while it is
 * being written or revised, feedback_viewed when it has feedback. Each once
 * per submission per tab.
 */
export const trackSubmissionOpened = safely((submission, pageMode) => {
  if (!submission?.id) return;
  const kind = submissionKind(submission);
  const withFeedback = hasFeedback(submission);
  if (pageMode === 'DRAFT' || pageMode === 'REVISE') {
    trackOnce(`draft_opened:${submission.id}`, 'draft_opened', {
      draft_id: submission.id,
      kind,
      has_feedback: withFeedback,
    });
  }
  if (withFeedback) {
    trackOnce(`feedback_viewed:${submission.id}`, 'feedback_viewed', {
      submission_id: submission.id,
      kind,
      feedback_by: feedbackBy(submission),
      status: statusBucket(submission),
    });
  }
});

/**
 * A comment on a submission was opened (clicked). Once per comment per tab.
 * Only the author's kind is sent; the reviewer's name is read, never sent.
 */
export const trackCommentOpened = safely((submissionId, comment) => {
  if (!comment?.id) return;
  const reviewer = String(comment.reviewerName ?? '');
  const author = /jedd\s*ai/i.test(reviewer)
    ? 'jeddai'
    : reviewer
    ? 'teacher'
    : 'unknown';
  trackOnce(`comment_opened:${comment.id}`, 'feedback_comment_opened', {
    submission_id: submissionId,
    author,
  });
});

// Send times, so the reveal can say how long the wait was and whether the
// person was still on the page when the feedback arrived.
const sentAt = new Map();

/** A draft was sent to JeddAI for feedback. */
export const trackDraftSent = safely((submission, tools = {}) => {
  if (!submission?.id) return;
  sentAt.set(submission.id, Date.now());
  track('draft_sent', {
    draft_id: submission.id,
    kind: submissionKind(submission),
    resubmit: hasFeedback(submission),
    rubric: Boolean(tools.markingCriteriaId),
    success_criteria: Array.isArray(tools.successCriteriaIds)
      ? tools.successCriteriaIds.length > 0
      : Boolean(tools.successCriteriaIds),
    comment_bank: Boolean(tools.commentBankId),
    short_answer: Boolean(tools.shortAnswerId),
  });
});

/** JeddAI feedback for a sent draft has just been shown. */
export const trackFeedbackRevealed = safely((submissionId) => {
  if (!submissionId) return;
  const startedAt = sentAt.get(submissionId);
  sentAt.delete(submissionId);
  track('jeddai_feedback_revealed', {
    draft_id: submissionId,
    seen_live: startedAt != null,
    ...(startedAt != null
      ? { wait_ms: Math.max(0, Date.now() - startedAt) }
      : {}),
  });
});

const TOOL_TYPES = {
  markingCriteria: 'rubric',
  rubric: 'rubric',
  successCriteria: 'success_criteria',
  commentBank: 'comment_bank',
  shortAnswer: 'short_answer',
};

/** A marking tool was chosen or cleared by hand. */
export const trackMarkingToolPicked = safely((toolType, toolId, context) => {
  const tool_type = TOOL_TYPES[toolType] || toolType;
  const ids = Array.isArray(toolId) ? toolId : [toolId];
  const id = ids.find((value) => value != null && value !== '');
  track('marking_tool_picked', {
    tool_type,
    context,
    action: id ? 'picked' : 'cleared',
    ...(id ? { marking_tool_id: String(id) } : {}),
  });
});

const firstId = (value) => {
  const ids = Array.isArray(value) ? value : [value];
  return ids.find((id) => id != null && id !== '') ?? null;
};

const sameIds = (a, b) => {
  const list = (value) =>
    (Array.isArray(value) ? value : [value])
      .filter((id) => id != null && id !== '')
      .map(String)
      .sort()
      .join(',');
  return list(a) === list(b);
};

/**
 * One marking_tool_picked per tool type whose selection changed between
 * `before` and `after` ({ markingCriteria, commentBank, successCriteria,
 * shortAnswer }, each an id or a list of ids).
 */
export const trackMarkingToolChanges = safely(
  (before = {}, after = {}, context) => {
    Object.keys(TOOL_TYPES).forEach((key) => {
      if (!(key in after) || sameIds(before[key], after[key])) return;
      trackMarkingToolPicked(key, firstId(after[key]), context);
    });
  }
);

// BulkUploadFlow's STEPS values → bulk_upload_step's `step`.
const BULK_STEPS = {
  opened: 'opened',
  entry: 'settings',
  question: 'question',
  dragDrop: 'files',
  converting: 'converting',
  organise: 'assign',
  review: 'review',
  marking: 'marking',
  submit: 'marking',
  generating: 'submitted',
  done: 'done',
  exited: 'exited',
};

/** A bulk upload wizard reached `step` (a BulkUploadFlow STEPS value). */
export const trackBulkUploadStep = safely(
  (step, { assignmentId, fileCount, studentCount } = {}) => {
    const mapped = BULK_STEPS[step];
    if (!mapped) return;
    track('bulk_upload_step', {
      step: mapped,
      assignment_id: assignmentId || undefined,
      file_count: fileCount,
      student_count: studentCount,
    });
  }
);

// TourContext keys → tour names in events.
const TOURS = {
  marking: 'marking',
  draft: 'draft',
  newTask: 'new_task',
  feedback: 'feedback',
  wizard: 'wizard',
};

let explicitTourEnd = null;

/** A tour was finished or skipped from its own controls. */
export const trackTourEnded = safely((tourKey, outcome, stepIndex) => {
  if (!TOURS[tourKey]) return;
  explicitTourEnd = tourKey;
  track('tour_ended', { tour: TOURS[tourKey], outcome, step_index: stepIndex });
});

/**
 * Compares the tour state with what was last seen: a step event when the
 * active tour or its step changes, and 'left' when a tour stops without its
 * own Finish or Skip (navigating away, the mobile takeover). Returns the new
 * "last seen" value for the caller to keep.
 */
export const trackTourTransition = safely(
  (previous, tourState) => {
    const active =
      Object.keys(TOURS).find((key) => tourState?.[`${key}TourActive`]) || null;
    const step = active ? tourState[`${active}TourStep`] : null;
    if (previous?.tour && previous.tour !== active) {
      if (explicitTourEnd !== previous.tour) {
        track('tour_ended', {
          tour: TOURS[previous.tour],
          outcome: 'left',
          step_index: previous.step,
        });
      }
      explicitTourEnd = null;
    }
    if (active && (active !== previous?.tour || step !== previous?.step)) {
      track('tour_step_viewed', { tour: TOURS[active], step_index: step });
    }
    return { tour: active, step };
  },
  (previous) => previous || { tour: null, step: null }
);

/** Test-only. */
export const __resetMappersForTests = () => {
  sentAt.clear();
  explicitTourEnd = null;
};
